import { useState, useEffect } from 'react';
import { auth } from './firebase'; // Import Firebase auth
import { onIdTokenChanged, getIdToken } from 'firebase/auth';

const useAuthManagement = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check token validity without network
  const isTokenValid = (token) => {
    if (!token) return false;

    try {
      // Decode the JWT token
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(window.atob(base64));

      // Check if token has expired
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    } catch (error) {
      console.error('Error checking token:', error);
      return false;
    }
  };

  // Save token to localStorage
  const saveToken = (userData) => {
    if (userData.token) {
      localStorage.setItem('authToken', userData.token);
      setIsAuthenticated(true);
    } else {
      localStorage.removeItem('authToken');
      setIsAuthenticated(false);
    }
  };

  // Clear token from localStorage
  const clearToken = () => {
    localStorage.removeItem('authToken');
    setIsAuthenticated(false);
  };

  // Refresh token periodically
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (user) {
        try {
          const newIdToken = await getIdToken(user, true); // Force refresh the token
          saveToken({ token: newIdToken });
        } catch (error) {
          console.error('Error refreshing ID token:', error);
          clearToken();
        }
      } else {
        clearToken();
      }
    });

    return () => unsubscribe();
  }, []);

  // Check for existing token on mount
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('authToken');
      if (isTokenValid(token)) {
        setIsAuthenticated(true);
      } else {
        clearToken();
      }
    };

    checkAuth();
  }, []);

  return {
    isAuthenticated,
    saveToken,
    clearToken,
  };
};

export default useAuthManagement;